import React, { useEffect, useMemo, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import SelectMenu from "@hl/base-components/lib/SelectMenu";
import {
  Beaker02,
  CodeBrowser,
  Cube,
  Database03,
  Grid01,
  Hexagon01,
  HomeLine,
  Image01,
  Infinity,
  LayersThree01,
} from "@hl/base-components/lib/assets/icons.generated";
import { ErrorWidget } from "@hl/base-components/lib/error";
import CustomPaginationWrapper from "@hl/base-components/lib/table/CustomPaginationWrapper";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import usePageVisitedEvent from "@hl/shared-features/lib/hooks/usePageVisitedEvent";
import { getNativeCurrencyIcon } from "@hl/shared-features/lib/utils/currency";
import { Group, SimpleGrid, Stack } from "@mantine/core";
import { capitalize } from "lodash";
import { Link } from "react-router-dom";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";

import {
  _CollectionType,
  CollectionFilters,
  CollectionSortableField,
  CollectionSortDirection,
  CollectionStatusFilter,
  SupportedMainnets,
} from "apollo/graphql.generated";
import { mapDashboardCollectionToEntity } from "components/BlockchainEntityTitle/types";
import { TOOLS_URL } from "config";
import CollectionCard from "~/features/collections/sections/ContractCollectionDashboard/CollectionCard";
import { useGetDashboardCollectionsQuery } from "~/features/collections-dashboard/queries.graphql.generated";
import CreateFirstCollection from "~/features/dashboard/CreateFirstCollection";
import DashboardContent from "~/features/dashboard/DashboardContent";
import NoCollectionsFoundForFilter from "~/features/dashboard/NoCollectionsFoundForFilter";

const PAGE_SIZE = 9;
interface SortConfig {
  name: string;
  field: CollectionSortableField;
  direction: CollectionSortDirection;
}

const SORT_CONFIG: Record<string, SortConfig> = {
  NEWEST: {
    name: "Newest",
    field: CollectionSortableField.CREATED_DATE,
    direction: CollectionSortDirection.DESC,
  },
  OLDEST: {
    name: "Oldest",
    field: CollectionSortableField.CREATED_DATE,
    direction: CollectionSortDirection.ASC,
  },
  ALPHABETICAL: {
    name: "Alphabetical",
    field: CollectionSortableField.NAME,
    direction: CollectionSortDirection.ASC,
  },
};

export const CollectionsDashboard = () => {
  const { isMobile } = useMantineMediaQueries();
  const [activePage, setPage] = useState(1);
  const [activeSort, setActiveSort] = useState(SORT_CONFIG.NEWEST);
  const [activeFilter, setActiveFilter] = useState<CollectionFilters>();
  const hasFilters = useMemo(
    () => !!activeFilter && Object.values(activeFilter).some((entry) => entry),
    [activeFilter]
  );

  const activeContractType = useMemo(() => {
    return activeFilter?.collectionTypes?.[0];
  }, [activeFilter]);

  const activeBlockchain = useMemo(
    () => activeFilter?.blockchains?.[0] || undefined,
    [activeFilter]
  );

  const { data, loading, error } = useGetDashboardCollectionsQuery({
    //TODO: Refresh cache on create collection
    fetchPolicy: "network-only",
    variables: {
      page: activePage,
      limit: PAGE_SIZE,
      sortBy: activeSort.field,
      sortDirection: activeSort.direction,
      filters: activeFilter ?? null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const collections = data?.getDashboardCollections?.collections ?? [];
  const pageInfo = data?.getDashboardCollections?.pageInfo;

  usePageVisitedEvent("COLLECTIONS");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activePage]);

  const resetPage = () => {
    setPage(1);
  };

  return (
    <DashboardContent>
      <AppSEO title="Collections" />
      <Stack spacing={48} h="100%">
        <Group position="apart">
          <Group spacing="sm" w={"100%"}>
            <SelectMenu
              title="Status"
              selectedItem={activeFilter?.status || undefined}
              w={isMobile ? "100%" : 200}
              items={[
                {
                  label: "All statuses",
                  selectedLabel: "All",
                  onClick: () => {
                    resetPage();
                    setActiveFilter({
                      ...activeFilter,
                      status: undefined,
                    });
                  },
                },
                ...Object.values(CollectionStatusFilter)
                  .filter(
                    (status) => status !== CollectionStatusFilter.ARCHIVED
                  )
                  .map((status) => {
                    const label = capitalize(status);
                    return {
                      label,
                      selectedLabel: label,
                      onClick: () => {
                        resetPage();
                        setActiveFilter({
                          ...activeFilter,
                          status,
                        });
                      },
                    };
                  }),
              ]}
              bottomItems={[
                {
                  label: "Archive",
                  selectedLabel: "Archive",
                  onClick: () => {
                    resetPage();
                    setActiveFilter({
                      ...activeFilter,
                      status: CollectionStatusFilter.ARCHIVED,
                    });
                  },
                },
              ]}
            />
            <SelectMenu
              title="Blockchain"
              selectedItem={activeBlockchain}
              w={isMobile ? "100%" : 200}
              items={[
                {
                  label: (
                    <Group>
                      <Cube width={16} height={16} />
                      All networks
                    </Group>
                  ),
                  value: undefined,
                  selectedLabel: "All",
                  onClick: () => {
                    resetPage();
                    setActiveFilter({
                      ...activeFilter,
                      blockchains: undefined,
                    });
                  },
                },
                ...Object.values(SupportedMainnets).map((chain) => {
                  const label = capitalize(chain);
                  return {
                    label: (
                      <Group>
                        {getNativeCurrencyIcon(chain, 16)}
                        {label}
                      </Group>
                    ),
                    value: chain,
                    selectedLabel: label,
                    onClick: () => {
                      resetPage();
                      setActiveFilter({
                        ...activeFilter,
                        blockchains: [chain],
                      });
                    },
                  };
                }),
              ]}
            />
            <SelectMenu
              title="Network"
              selectedItem={
                activeFilter?.testnet == undefined
                  ? "All networks"
                  : activeFilter.testnet
                  ? "Testnet"
                  : "Mainnet"
              }
              w={isMobile ? "100%" : 200}
              items={[
                ...[
                  {
                    label: "All networks",
                    selectedLabel: "All",
                    testnet: undefined,
                    icon: <Database03 />,
                  },
                  { label: "Mainnet", testnet: false, icon: <HomeLine /> },
                  { label: "Testnet", testnet: true, icon: <Beaker02 /> },
                ].map(({ label, selectedLabel, testnet, icon }) => ({
                  label: (
                    <Group>
                      {icon}
                      {label}
                    </Group>
                  ),
                  value: label,
                  selectedLabel: selectedLabel || label,
                  onClick: () => {
                    resetPage();
                    setActiveFilter({
                      ...activeFilter,
                      testnet,
                    });
                  },
                })),
              ]}
            />
            <SelectMenu
              title="Type"
              selectedItem={activeContractType}
              w={isMobile ? "100%" : 200}
              items={[
                ...[
                  {
                    label: "All collection types",
                    selectedLabel: "All",
                    collectionTypes: undefined,
                    icon: <LayersThree01 />,
                  },
                  {
                    label: "Generative",
                    collectionTypes: [_CollectionType.GenerativeSeries],
                    icon: <CodeBrowser width={16} height={16} />,
                  },
                  {
                    label: "Series",
                    collectionTypes: [_CollectionType.Series],
                    icon: <Image01 />,
                  },
                  {
                    label: "Limited edition",
                    collectionTypes: [_CollectionType.LimitedEdition],
                    icon: <Grid01 />,
                  },
                  {
                    label: "Open edition",
                    collectionTypes: [_CollectionType.OpenEdition],
                    icon: <Infinity />,
                  },
                  {
                    label: "One of one",
                    collectionTypes: [_CollectionType.OneOfOne],
                    icon: <Hexagon01 />,
                  },
                ].map(({ label, selectedLabel, collectionTypes, icon }) => ({
                  label: (
                    <Group>
                      {icon}
                      {label}
                    </Group>
                  ),
                  value: collectionTypes?.[0],
                  selectedLabel: selectedLabel || label,
                  onClick: () => {
                    resetPage();
                    setActiveFilter({
                      ...activeFilter,
                      collectionTypes,
                    });
                  },
                })),
              ]}
            />
          </Group>
          <SelectMenu
            title="Sort by"
            w={isMobile ? "100%" : 200}
            items={Object.values(SORT_CONFIG).map((config) => ({
              label: config.name,
              onClick: () => {
                resetPage();
                setActiveSort(config);
              },
            }))}
          />
        </Group>
        {loading ? (
          <LoadingPage />
        ) : error ? (
          <ErrorWidget />
        ) : !pageInfo || !collections.length ? (
          hasFilters ? (
            <NoCollectionsFoundForFilter
              clearFilters={() => {
                setActiveFilter(undefined);
              }}
            />
          ) : (
            <CreateFirstCollection />
          )
        ) : (
          <CustomPaginationWrapper
            activePage={activePage}
            setPage={setPage}
            maxLength={0}
            itemsPerPage={PAGE_SIZE}
            border={false}
            withControls
          >
            <SimpleGrid
              cols={3}
              spacing={80}
              breakpoints={[
                { maxWidth: "lg", cols: 3, spacing: 40, verticalSpacing: 40 },
                { maxWidth: "md", cols: 2, spacing: 20, verticalSpacing: 20 },
                {
                  maxWidth: "xs",
                  cols: 1,
                  spacing: "xs",
                  verticalSpacing: "xs",
                },
              ]}
            >
              {collections.map(mapDashboardCollectionToEntity).map((entity) => (
                <Link
                  key={entity.id}
                  style={{ textDecoration: "inherit", color: "inherit" }}
                  to={(entity.isDraft
                    ? TOOLS_URL.collections.resumeCreate
                    : TOOLS_URL.collections.manage
                  ).replace(":collectionId", entity.id)}
                >
                  <CollectionCard blockchainEntity={entity} />
                </Link>
              ))}
            </SimpleGrid>
          </CustomPaginationWrapper>
        )}
      </Stack>
    </DashboardContent>
  );
};

export default CollectionsDashboard;
